<template>
  <div class="share-by-magic-link-wrapper p-1">
    <p class="title">{{$tf('Share by Magic Link')}}</p>
    <p class="msg-info">{{$tf('Anyone with the link and PIN will be able to view')}}</p>
    <v-progress-linear
      v-show="loading"
      height="2"
      indeterminate
    />
    <transition name="fade">
      <div v-if="!loading">
        <div v-if="!magicLinkIsEnabled">
          <v-layout
            v-if="$store.state.config.enableMLPassword"
            class="mt-2"
            row
            wrap
            align-center
          >
            <v-flex xs12 sm6>
              <v-checkbox
                :label="$tf('Protect Magic Link with password')"
                v-model="withPassword" />
            </v-flex>
            <v-flex xs12 sm6 fill-height>
              <v-text-field
                v-model="password"
                :label="$tf('Password')"
                ref="password"
                name="password"
                :append-icon="showPassword ? 'visibility_off' : 'visibility'"
                :append-icon-cb="() => { showPassword = !showPassword }"
                :type="showPassword ? 'text' : 'password'"
                :style="{
                  'visibility': withPassword ? 'visible' : 'hidden'
                }"
                :error-messages="passwordErrors"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="none"
                spellcheck="false"
              />
            </v-flex>
          </v-layout>
          <v-layout v-if="$vuetify.breakpoint.xsOnly && magicLinkExpired" wrap class="get-magic-link-wrapper">
            <v-flex class="d-flex" xs12 sm6>
              <v-btn depressed @click="createMagicLink" class="primary" :disabled="magicLinkLoading">
                <v-avatar class="avatar-icon" size="34">
                  <img :src="($theme.themes.magic_link === 'theme--dark' ) ? '/static/images/magic-link-dark.svg' : '/static/images/magic-link.svg'" alt="Magic Link Icon" />
                </v-avatar>
                {{$tf('Get Magic Link')}}
              </v-btn>
            </v-flex>
            <v-flex class="d-flex" xs12 sm6 v-if="magicLinkExpired">
              <v-btn depressed @click="restoreMagicLink" color="primary" flat="flat" :disabled="magicLinkLoading">
                <v-icon class="mr-2">replay</v-icon>
                {{$tf('Restore Magic Link')}}
              </v-btn>
            </v-flex>
          </v-layout>
          <div v-else class="get-magic-link-wrapper">
            <v-btn depressed @click="createMagicLink" class="primary" :disabled="magicLinkLoading">
              <v-avatar class="avatar-icon" size="34">
                <img :src="($theme.themes.magic_link === 'theme--dark' ) ? '/static/images/magic-link-dark.svg' : '/static/images/magic-link.svg'" alt="Magic Link Icon" />
              </v-avatar>
              {{$tf('Get Magic Link')}}
            </v-btn>
            <v-btn v-if="magicLinkExpired" depressed @click="restoreMagicLink" color="primary" flat="flat" :disabled="magicLinkLoading">
              <v-icon class="mr-2">replay</v-icon>
              {{$tf('Restore Magic Link')}}
            </v-btn>
          </div>
        </div>
        <div v-else>
          <!--  -->
          <div class="magic-link-wrapper">
            <div class="link-info">
              <div class="link-pin-copy">
                <div class="key">{{$tf('URL')}}</div>
                <div class="value">
                  <span>{{magicLinkURL}}</span>
                </div>
                <div class="action">
                  <v-btn flat @click="() => { copyToClipboard(magicLinkURL) }">
                    <v-icon class="mr-2">content_copy</v-icon>
                    {{$tf('Copy')}}
                  </v-btn>
                </div>
              </div>
              <div class="link-pin-copy">
                <div class="key">{{$tf('PIN')}}</div>
                <div class="value">
                  <span>{{magicLinkSSID}}</span>
                </div>
                <div class="action">
                  <v-btn flat @click="() => { copyToClipboard(magicLinkSSID) }">
                    <v-icon class="mr-2">content_copy</v-icon>
                    {{$tf('Copy')}}
                  </v-btn>
                </div>
              </div>
              <div v-if="sharedWith.public_url.has_password" class="caption grey--text text--darken-1 pt-1 pb-1">
                {{ $tf('MagicLinkPasswordMessage') }}
              </div>
            </div>
          </div>
          <!--  -->
          <div class="sharing-buttons">
              <v-btn flat color="red" @click="deleteMagickLink">
                <v-icon left>delete</v-icon>
                <span>{{$tf('delete magic link')}}</span>
              </v-btn>

            <div v-if="$machine.os !== 'ios'">
              <v-btn v-if="shareNativeCompatible()" icon @click="shareNative">
                <v-icon>{{'share'}}</v-icon>
              </v-btn>
              <v-btn
                v-else
                depressed
                flat
                @click="openShareEmailDialog"
              >
                <v-icon left>email</v-icon>
                {{$tf('email')}}
              </v-btn>
            </div>
            <div v-else>
              <v-btn v-if="shareNativeCompatible()" icon @click="shareNativeIOS">
                <v-icon>{{'share'}}</v-icon>
              </v-btn>
              <v-btn
                v-else
                depressed
                flat
                @click="openShareEmailDialog"
              >
                <v-icon left>{{'email'}}</v-icon>
                {{$tf('email')}}
              </v-btn>
            </div>
            <v-btn depressed flat @click="printMagicLink">
              <v-icon left>print</v-icon>
              {{$tf('print')}}
            </v-btn>
              <v-btn depressed @click="copyToClipboard(bodyCopy)">
                <v-icon left>content_copy</v-icon>
                <span>{{$tf('Copy All')}}</span>
            </v-btn>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  copyToClipboard,
  shareNative,
  shareNativeCompatible
} from '~/helpers/browser'
import Api from '~/store/idonia-api/api.js'
import MainBus from '~/bus'
import CryptoJS from 'crypto-js'

export default {
  name: 'ShareMagicLinkCard',
  props: {
    flat: {
      type: Boolean,
      default: true
    },
    file: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    sharedWith: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    magicLinkIsEnabled () {
      return this.sharedWith && this.sharedWith.public_url && this.sharedWith.public_url.public_url_id && !this.sharedWith.public_url.is_expired
    },
    magicLinkExpired () {
      return this.sharedWith && this.sharedWith.public_url && this.sharedWith.public_url.is_expired
    },
    fileType () {
      switch (this.file.type) {
        case 'directory':
          return this.$tf('this directory')
        case 'document':
        case 'document_vault':
          return this.$tf('this file')
        case 'container_dicom':
        case 'dicom_vault':
          return this.$tf('medical images')
        default:
          return this.$tf(this.file.type)
      }
    },
    subjectMail () {
      return encodeURIComponent(
        this.$store.state.session.account.firstname +
          ' ' +
          this.$store.state.session.account.lastname +
          ' ' +
          this.$tf('emailSubjectShare', { type: this.fileType })
      )
    },
    bodyCopy () {
      return (
        this.$tf('emailNativeShare', {
          filename: this.file.name,
          code: this.magicLinkSSID
        }) +
        `\n\n` +
        this.magicLinkURL
      )
    },
    bodyMail () {
      return encodeURIComponent(
        this.$tf('emailNativeShare', {
          filename: this.file.name,
          code: this.magicLinkSSID
        }) +
          `\n\n` +
          this.magicLinkURL
      )
    },
    magicLinkURL () {
      return this.magicLinkIsEnabled
        ? `${window.location.origin}/v/${this.sharedWith.public_url.namespace}`
        : `${window.location.origin}/v/${this.$store.state.session.account.username}`
    },
    magicLinkSSID () {
      return this.magicLinkIsEnabled
        ? this.sharedWith.public_url.secure_id
        : null
    }
  },
  data () {
    return {
      withPassword: false,
      showPassword: false,
      password: '',
      passwordErrors: [],
      magicLinkLoading: false
    }
  },
  watch: {
    password () {
      this.passwordErrors = []
    },
    withPassword () {
      this.password = ''
    },
    loading (val) {
      if (!val) {
        this.magicLinkLoading = false
      }
    }
  },
  methods: {
    async openShareEmailDialog () {
      await this.$store.dispatch('dialog/closeDialog')
      await this.$store.dispatch('dialog/showDialog', {
        type: 'share-email',
        props: {
          publicUrlId: this.sharedWith.public_url.public_url_id,
          file: this.file
        }
      })
    },
    printMagicLink () {
      window.open('/print/file/' + this.file.id_file, '_blank')
    },
    shareNativeCompatible () {
      return shareNativeCompatible()
    },
    shareNative () {
      shareNative({
        url: this.magicLinkURL,
        text: this.$tf('emailNativeShare', {
          filename: this.file.name,
          code: this.magicLinkSSID
        }),
        title: `${this.$store.getters['session/accountName']} ${this.$tf(
          'wants to share a file with you'
        )} | Idonia`
      })
    },
    shareNativeIOS () {
      // TODO
      shareNative({
        text: this.magicLinkURL + '#' + this.magicLinkSSID
      })
    },
    copyToClipboard (text) {
      copyToClipboard(text)
        .then(() => {
          this.$store.dispatch('feedback/addSnack', {
            text: 'Copied to clipboard'
          })
        })
        .catch(error => {
          this.$store.dispatch('feedback/addSnack', {
            text: error.message,
            type: 'error'
          })
        })
    },
    copyFullURLToClipboard () {
      copyToClipboard(this.magicLinkURL + '#' + this.magicLinkSSID)
        .then(() => {
          this.$store.dispatch('feedback/addSnack', {
            text: 'Copied to clipboard'
          })
        })
        .catch(error => {
          this.$store.dispatch('feedback/addSnack', {
            text: error.message,
            type: 'error'
          })
        })
    },
    createMagicLink () {
      if (this.file && this.sharedWith && (!this.sharedWith.public_url || this.sharedWith.public_url.is_expired)) {
        if (this.withPassword && this.password.length === 0) {
          this.passwordErrors = [this.$tf('Field required', { field: this.$tf('Password') })]
          this.$refs.password.focus()
          return
        }

        this.magicLinkLoading = true
        this.$store.dispatch('dialog/setLoading', true)
        MainBus.$emit('analytics', {
          action: 'create-magic-link',
          arguments: {
            file_id: this.file.id_file
          }
        })
        Api.postV2('public-url', {
          data: {
            namespace: this.$store.state.session.account.username,
            file_id: this.file.id_file,
            password: (this.password.length > 0 && this.withPassword)
              ? CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(CryptoJS.SHA256(this.password).toString()))
              : null,
            expired_creation_mode: 'create'
          }
        })
          .then(response => {
            if (response.ok) {
              this.$store.dispatch('feedback/addSnack', {
                text: 'Magic link enabled',
                action: {
                  label: 'Share',
                  handler: this.shareNativeCompatible()
                    ? this.shareNative
                    : this.copyFullURLToClipboard
                }
              })
            }
          })
          .catch(response => {
            if (response && response.body && response.body.error) {
              this.$store.dispatch('feedback/addSnack', {
                text: response.body.error,
                type: 'error'
              })
            }
          })
          .finally(() => {
            this.$store.dispatch('dialog/setLoading', false)
          })
      }
    },
    restoreMagicLink () {
      if (this.file && this.sharedWith && this.sharedWith.public_url && this.sharedWith.public_url.is_expired) {
        this.$store.dispatch('dialog/unshiftDialog', {
          title: 'Expired Magic Link',
          body: require('~/components/common/dialog/types/DialogExpiredMagicLink.vue').default,
          props: {
            sharedWith: this.sharedWith
          },
          persistent: true,
          width: 600,
          actions: {
            cancel: 'Close',
            accept: {
              title: 'Restore',
              callback: () => {
                this.magicLinkLoading = true
                this.$store.dispatch('dialog/setLoading', true)
                Api.postV2('public-url', {
                  data: {
                    namespace: this.$store.state.session.account.username,
                    file_id: this.file.id_file,
                    password: (this.password.length > 0 && this.withPassword)
                      ? CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(CryptoJS.SHA256(this.password).toString()))
                      : null,
                    expired_creation_mode: 'update'
                  }
                })
                  .then(response => {
                    if (response.ok) {
                      this.$store.dispatch('feedback/addSnack', {
                        text: 'Magic link enabled',
                        action: {
                          label: 'Share',
                          handler: this.shareNativeCompatible()
                            ? this.shareNative
                            : this.copyFullURLToClipboard
                        }
                      })
                    }
                  })
                  .catch(response => {
                    if (response && response.body && response.body.error) {
                      this.$store.dispatch('feedback/addSnack', {
                        text: response.body.error,
                        type: 'error'
                      })
                    }
                  })
                  .finally(() => {
                    this.magicLinkLoading = false
                    this.$store.dispatch('dialog/setLoading', false)
                  })
              }
            }
          }
        })
      }
    },
    deleteMagickLink () {
      if (this.file && this.sharedWith.public_url !== null) {
        const oldShared = Object.assign({}, this.sharedWith)
        this.$emit('set-shared-with', {
          accounts: oldShared.accounts,
          groups: oldShared.groups,
          guests: oldShared.guests,
          organizations: oldShared.organizations
        })
        this.magicLinkLoading = true
        this.$store.dispatch('feedback/addSnack', {
          text: 'Magic link disabled',
          action: {
            label: 'undo',
            handler: () => {
              this.$emit('set-shared-with', oldShared)
              this.magicLinkLoading = false
            }
          },
          callback: async () => {
            this.$store.dispatch('dialog/setLoading', true)
            try {
              const response = await Api.deleteV2(
                `public-url/${this.$store.state.session.account.account_id}/${this.file.id_file}`
              )
              if (response.ok) {
                this.magicLinkLoading = false
              }
            } catch (response) {
              if (response && response.body && response.body.error) {
                this.$store.dispatch('feedback/addSnack', {
                  text: response.body.error,
                  type: 'error'
                })
                this.magicLinkLoading = false
              }
            } finally {
              this.$store.dispatch('dialog/setLoading', false)
            }
          }
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.title {
  font-weight: bold;
  font-size: 1.4rem;
}
.get-magic-link-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem;
}
.magic-link-wrapper {
  display: flex;
  flex-direction: column;

  .link-info {
    flex-grow: 1;
    .link-pin-copy {
      background-color: #F5F5F5;
      color: #333;
      display: flex;
      align-items: center;
      flex-grow: 1;
      border-radius: 2px;
      margin: 0.5rem 0;
      padding: 0;
      font-size: 18px;
      .key {
        font-weight: bold;
        margin-left: 1rem;
        width: 5rem;
      }
      .value {
        width: 5rem;
        flex-grow: 1;
        font-weight: normal;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  .buttons {
    display: flex;
    justify-content: space-between;
  }
}
.msg-info {
  margin: 0;
  padding: 0;
}

.sharing-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  & > *:first-child {
    margin-right: auto; // Delete button aligned to the left
  }
}
.avatar-icon {
  padding: 0.25rem;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/common/share/ShareMagicLinkCard.vue